import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/lib/store/auth";
import { useSignInForm } from "@/hooks/use-sign-in-form";
import { Alert, AlertDescription } from "@/components/ui/alert";

const SignIn = () => {
  const navigate = useNavigate();
  const { session, error } = useAuthStore();
  const { 
    email, 
    setEmail, 
    password, 
    setPassword, 
    loading, 
    handleSubmit 
  } = useSignInForm('caregiver');

  // If already authenticated, redirect
  useEffect(() => {
    if (session) {
      navigate('/dashboard', { replace: true });
    }
  }, [session, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 text-slate-900 p-6">
      <Link to="/" className="inline-flex items-center text-blue-primary hover:text-blue-600 mb-12 font-medium">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Home
      </Link>

      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
        {error && (
          <Alert variant="destructive" className="bg-red-50 border-2 border-red-500 text-red-900 animate-in fade-in slide-in-from-top-2 duration-300">
            <AlertDescription className="font-medium text-base">{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Caregiver Login</h1>
          <p className="text-slate-600">Sign in to connect with sleep consultants</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-slate-700 font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white border-slate-300 text-slate-900 focus:border-blue-primary focus:ring-blue-100"
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-slate-700 font-medium">
              Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-white border-slate-300 text-slate-900 focus:border-blue-primary focus:ring-blue-100"
              placeholder="••••••••"
              disabled={loading}
            />
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-blue-primary hover:text-blue-600">
                Forgot password?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            variant="high-contrast"
            className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="text-center text-slate-600 mt-4">
          Don't have an account yet?{" "}
          <Link to="/caregiver-registration" className="text-blue-primary hover:text-blue-600 font-medium">
            Sign up
          </Link>
        </div>

        <div className="border-t border-slate-200 pt-4 text-center">
          <p className="text-slate-600 mb-2">Are you a sleep professional?</p>
          <Link to="/sleep-universities" className="text-blue-primary hover:text-blue-600 font-medium inline-flex items-center">
            <span>Become a Sleep Coach</span>
            <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
